import React from "react";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { TriageData } from "../TriageWizard";
import { useLanguage } from "@/contexts/LanguageContext";

interface RedFlagsStepProps {
  data: TriageData;
  updateData: (data: TriageData) => void;
  prefilled?: boolean;
}

const EMERGENCY_SYMPTOM_KEYS = [
  { key: 'severeBleeding', id: 'severe_bleeding', critical: true },
  { key: 'unconscious', id: 'unconscious', critical: true },
  { key: 'difficultyBreathingSevere', id: 'difficulty_breathing_severe', critical: true },
  { key: 'chestPainSevere', id: 'chest_pain_severe', critical: true },
  { key: 'neurological', id: 'neurological', critical: true },
  { key: 'severeInjury', id: 'severe_injury', critical: true },
  { key: 'allergicReaction', id: 'allergic_reaction', critical: true },
  { key: 'suicidalThoughts', id: 'suicidal_thoughts', critical: true },
  { key: 'highFever', id: 'high_fever', critical: false },
  { key: 'severeAbdominalPain', id: 'severe_abdominal_pain', critical: false },
  { key: 'severeHeadache', id: 'severe_headache', critical: false },
  { key: 'visionChanges', id: 'vision_changes', critical: false },
] as const;

const RedFlagsStep: React.FC<RedFlagsStepProps> = ({ data, updateData }) => {
  const { t } = useLanguage();

  const toggleEmergencySymptom = (symptom: string) => {
    const emergency_symptoms = data.emergency_symptoms.includes(symptom)
      ? data.emergency_symptoms.filter(s => s !== symptom)
      : [...data.emergency_symptoms, symptom];
    updateData({ ...data, emergency_symptoms });
  };

  const hasCriticalSymptoms = EMERGENCY_SYMPTOM_KEYS
    .filter(s => s.critical)
    .some(s => data.emergency_symptoms.includes(s.id));

  const getSymptomLabel = (key: string) => t.redFlags.symptoms[key as keyof typeof t.redFlags.symptoms] || key;

  return (
    <div className="space-y-6">
      <div>
        <Label className="text-base font-medium mb-4 block">{t.redFlags.title}</Label>
        <p className="text-sm text-muted-foreground mb-4">{t.redFlags.subtitle}</p>
      </div>

      {hasCriticalSymptoms && (
        <Alert className="border-red-200 bg-red-50">
          <AlertTriangle className="h-4 w-4 text-red-600" />
          <AlertDescription className="text-red-800">{t.redFlags.emergencyAlert}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-3">
          {EMERGENCY_SYMPTOM_KEYS.map(({ key, id, critical }) => (
            <div
              key={id}
              className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                data.emergency_symptoms.includes(id)
                  ? critical ? "bg-red-50 border-red-200" : "bg-orange-50 border-orange-200"
                  : "bg-white border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => toggleEmergencySymptom(id)}
            >
              <div className="flex items-start space-x-3">
                <div className={`w-4 h-4 rounded border-2 mt-0.5 flex items-center justify-center ${
                  data.emergency_symptoms.includes(id)
                    ? critical ? "bg-red-600 border-red-600" : "bg-orange-500 border-orange-500"
                    : "border-gray-300"
                }`}>
                  {data.emergency_symptoms.includes(id) && (
                    <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-medium ${critical ? "text-red-800" : "text-orange-800"}`}>{getSymptomLabel(key)}</p>
                  {critical && <p className="text-xs text-red-600 mt-1">{t.redFlags.criticalNote}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.emergency_symptoms.length === 0 && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">{t.redFlags.noEmergencyMessage}</p>
        </div>
      )}

      <div className="text-xs text-muted-foreground bg-gray-50 p-3 rounded-lg">
        <p><strong>{t.redFlags.disclaimer.split(':')[0]}:</strong> {t.redFlags.disclaimer.split(':').slice(1).join(':')}</p>
      </div>
    </div>
  );
};

export default RedFlagsStep;
